import type { MessageItem, Messages, UserMessageSignature } from '../../ReplicantMessages';
import type { APIAccessInterface } from '../../server/ServerReplicant';
import { generateMessageId } from '../../utils/MessageUtils';
import type { DelayedAction, PostMessageFn, PostMessagesMap, SharedStateDelayedAction } from '../ReplicantAPI';

export function generateFlushMessages(opts: {
    consistentFetchIds: string[];
    delayedActions: DelayedAction[];
    apiAccess: APIAccessInterface<any>;
}): () => Promise<void> {
    return async () => {
        const now = opts.apiAccess.dateNow();
        const flushedMessages: { [receiver: string]: MessageItem[] } = {};
        const flushedSharedStateActions: SharedStateDelayedAction[] = [];

        // Remove all async message posts from delayed actions and resolve them immediately:
        for (let i = opts.delayedActions.length - 1; i >= 0; i--) {
            const delayedAction = opts.delayedActions[i]!;

            if (
                (delayedAction.type === 'postMessage' || delayedAction.type === 'postSharedStateMessage') &&
                delayedAction.async
            ) {
                const receiverId =
                    delayedAction.type === 'postMessage' ? delayedAction.receiverId : delayedAction.stateId;

                if (delayedAction.type === 'postMessage') {
                    const message = delayedAction.messages[0];

                    if (!flushedMessages[receiverId]) {
                        flushedMessages[receiverId] = [];
                    }

                    // Write the message immediately without indexing:
                    flushedMessages[receiverId].push({
                        args: message.args,
                        id: message.id,
                        name: message.name,
                        sender: message.sender,
                        timestamp: now + message.senderClockOffset,
                    });

                    message.skipDbWrite = true; // Index the message in resolveDelayedActions without writing again to DB
                } else {
                    // Post the shared state message immediately but keep the flushed action in `delayedActions` queue so that the shared state gets indexed afer resolving the action batch:
                    delayedAction.flushed = true;
                    flushedSharedStateActions.push({ ...delayedAction, flushed: false });
                }

                // Fetch receiver state consistently in subsequent queries within the same action:
                if (!opts.consistentFetchIds.includes(receiverId)) {
                    opts.consistentFetchIds.push(receiverId);
                }
            }
        }

        const receiverPromises = Object.keys(flushedMessages).map((receiver) =>
            opts.apiAccess.writeMessages(receiver, flushedMessages[receiver]!),
        );

        await Promise.all([
            ...receiverPromises,

            opts.apiAccess.sharedStates.resolveAllSharedStatesUpdates(flushedSharedStateActions),
        ]);
    };
}

export function generateMessagesAPI<TMessages extends Messages>(opts: {
    senderId: string;
    dateNow: () => number;
    delayedActions: DelayedAction[];
    getClockOffset: () => number;
    async: boolean;
    messages?: TMessages;
}): PostMessagesMap<TMessages> {
    const { async, dateNow, delayedActions, getClockOffset, senderId, messages } = opts;

    const messagesAPI: PostMessagesMap<any> = {};

    if (!messages) {
        return messagesAPI;
    }

    function createPostMessageFn(name: string, msg: UserMessageSignature): PostMessageFn<any> {
        return (receiverId: string, args: any): void => {
            msg.schema.tryValidate(args);

            const now = dateNow() + getClockOffset();
            const counter = delayedActions.length;

            const id = generateMessageId(now, counter);

            delayedActions.push({
                async,
                type: 'postMessage',
                receiverId,
                messages: [{ id, name, args, sender: senderId, senderClockOffset: getClockOffset() }],
            });
        };
    }

    for (const [key, msg] of Object.entries(messages)) {
        const isNamespace = !('isAdmin' in msg && 'schema' in msg);

        if (isNamespace) {
            const namespace: Record<string, PostMessageFn<any>> = {};

            for (const [nestedKey, nestedMsg] of Object.entries(msg)) {
                if (nestedMsg.isAdmin) {
                    continue;
                }

                namespace[nestedKey] = createPostMessageFn(`${key}.${nestedKey}`, nestedMsg);
            }

            messagesAPI[key] = namespace;
        } else {
            if (msg.isAdmin) {
                continue;
            }

            messagesAPI[key] = createPostMessageFn(key, msg);
        }
    }

    return messagesAPI;
}
